import { useState, useMemo, useEffect, useCallback } from "react";
import { SmartPhone01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import type { StylesConfig } from "react-select";
import { Select } from "shared/components";
import type { BaseOption } from "shared/components";
import { formatPhone } from "shared/utils";
import { useAllPhoneNumbers } from "../../phone-numbers/lib/hooks";
import { useCrmContact } from "../api/crm";
import { useSendOptions, useSmsOptOuts } from "../api/hooks";
import { useUndeliverableNumbers } from "../api/use-undeliverable-numbers";
import {
  OPTED_OUT_SMS_MESSAGE,
  buildOptedOutSet,
  isPhoneOptedOut
} from "../utils/smsOptOut";
import type { UndeliverableNumber } from "../types";
import {
  buildUndeliverableMap,
  composerBlockTooltip,
  findUndeliverable
} from "shared/utils/sms-undeliverable";
import { RoutingFieldsRow } from "./RoutingFieldsRow";
import { useComposerNarrow } from "../context/useComposerNarrow";
import { useInboxEmbed } from "../context/useInboxEmbed";
import {
  FOOTER_PILL_STYLES,
  footerFromPillProps,
  footerPillClassName,
  withWarningBorder
} from "./participantFieldStyles";
import { ToRecipientsSelect } from "./ToRecipientsSelect";
import { TwoLineOptionLabel } from "./TwoLineOptionLabel";
import {
  CompactRoutingField,
  CompactRoutingEmpty,
  CompactRoutingRow
} from "./CompactRoutingField";
import { distinctPhoneName } from "../utils/phoneLabel";
import type { MultiRecipientControls } from "./multi-recipient";
import { digitsOnly } from "../utils/contactMethods";
import {
  useRecommendedSmsSender,
  useSenderMismatch
} from "../hooks/useRecommendedSender";
import {
  RecommendationReason,
  RecommendedTitle
} from "./recommendedSenderOption";
import { senderMenuWidth, type SenderOption } from "../utils/senderOption";
import { SenderMismatchDialog } from "./SenderMismatchDialog";

const PHONE_VALUE_ICON = (
  <HugeiconsIcon
    icon={SmartPhone01Icon}
    size={16}
    className="text-content-300 flex-shrink-0"
  />
);

interface SmsRoutingSelectProps {
  contactId?: string;
  conversationId?: string;
  onSelectionChange?: (
    phoneNumberId: string,
    toPhone: string,
    toState: {
      isToOptedOut: boolean;
      toUndeliverable: UndeliverableNumber | null;
    }
  ) => void;
  /** The composer sits at the bottom of /inbox, so menus open upward there. */
  menuPlacement?: "top" | "bottom" | "auto";
  /**
   * Pop-up participants block (CRM-1133 design): labeled boxed fields —
   * stacked on the floating card, side by side in the drawer.
   */
  compact?: boolean;
  multiRecipient?: MultiRecipientControls;
  recommendSender?: boolean;
}

export const SmsRoutingSelect = ({
  contactId,
  conversationId,
  onSelectionChange,
  menuPlacement = "top",
  compact = false,
  multiRecipient,
  recommendSender = false
}: SmsRoutingSelectProps) => {
  const embed = useInboxEmbed();
  const isNarrow = useComposerNarrow();
  const [selectedFrom, setSelectedFrom] = useState<string | null>(null);
  const [selectedTo, setSelectedTo] = useState<string | null>(null);

  const { data: contact } = useCrmContact(contactId || "");

  const { data: sendOptions } = useSendOptions(conversationId);
  const { data: optOutData } = useSmsOptOuts(conversationId || "");

  // null → no restriction (unrestricted user, or options not loaded yet).
  const allowedToPhones = useMemo(() => {
    if (!sendOptions || sendOptions.unrestricted) return null;
    return new Set(sendOptions.phones.map(digitsOnly));
  }, [sendOptions]);

  const optedOutSet = useMemo(
    () => buildOptedOutSet(optOutData?.opted_out_numbers),
    [optOutData]
  );

  // sms-opt-outs only covers numbers already texted in this thread.
  const contactPhones = useMemo(() => contact?.phones ?? [], [contact]);
  const contactUndeliverable = useUndeliverableNumbers(contactPhones);
  const undeliverableMap = useMemo(() => {
    const merged = buildUndeliverableMap(optOutData?.undeliverable_numbers);
    contactUndeliverable.forEach((status, key) => merged.set(key, status));
    return merged;
  }, [optOutData, contactUndeliverable]);

  const { data: phoneNumbersData, isLoading: isLoadingNumbers } =
    useAllPhoneNumbers();

  const activeNumbers = useMemo(
    () =>
      phoneNumbersData?.items.filter(
        (phoneNumber) => phoneNumber.status === "active" && phoneNumber.can_sms
      ) ?? [],
    [phoneNumbersData]
  );

  const sortedNumbers = useMemo(
    () =>
      [...activeNumbers].sort((first, second) =>
        (first.phone_number ?? "").localeCompare(second.phone_number ?? "")
      ),
    [activeNumbers]
  );

  const recommendation = useRecommendedSmsSender({
    enabled: recommendSender,
    conversationId,
    contact,
    numbers: sortedNumbers
  });

  const fromOptions: SenderOption[] = useMemo(
    () =>
      sortedNumbers.map((phoneNumber) => {
        const title = formatPhone(phoneNumber.phone_number ?? "");
        const subtitle = distinctPhoneName(
          phoneNumber.name,
          phoneNumber.phone_number ?? ""
        );
        const isRecommended =
          phoneNumber.communication_service_id === recommendation?.value;
        return {
          value: phoneNumber.communication_service_id,
          label: <TwoLineOptionLabel title={title} subtitle={subtitle} />,
          menuLabel:
            recommendation && isRecommended ? (
              <div className="min-w-0 space-y-1">
                <TwoLineOptionLabel
                  title={<RecommendedTitle title={title} />}
                  subtitle={subtitle}
                />
                <RecommendationReason reason={recommendation.reason} />
              </div>
            ) : undefined
        };
      }),
    [sortedNumbers, recommendation]
  );

  const phoneById = useMemo(
    () =>
      new Map(
        sortedNumbers.map((phoneNumber) => [
          phoneNumber.communication_service_id,
          formatPhone(phoneNumber.phone_number ?? "")
        ])
      ),
    [sortedNumbers]
  );

  const toOptions: BaseOption[] = useMemo(() => {
    if (!contact) return [];
    const contactName = [contact.first_name, contact.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();

    const visiblePhones = allowedToPhones
      ? contact.phones.filter((phone) => allowedToPhones.has(digitsOnly(phone)))
      : contact.phones;

    return visiblePhones.map((phone) => {
      const optedOut = isPhoneOptedOut(phone, optedOutSet);
      const undeliverable = findUndeliverable(phone, undeliverableMap);
      let disabledMessage: string | undefined;
      if (optedOut) disabledMessage = OPTED_OUT_SMS_MESSAGE;
      else if (undeliverable)
        disabledMessage = composerBlockTooltip(undeliverable, contactName);
      return {
        value: phone,
        isDisabled: optedOut || Boolean(undeliverable),
        disabledMessage,
        label: (
          <TwoLineOptionLabel
            title={formatPhone(phone)}
            subtitle={distinctPhoneName(contactName, phone)}
          />
        )
      };
    });
  }, [contact, allowedToPhones, optedOutSet, undeliverableMap]);

  const effectiveFrom = useMemo(() => {
    if (selectedFrom && phoneById.has(selectedFrom)) return selectedFrom;
    if (recommendation) return recommendation.value;
    return sortedNumbers[0]?.communication_service_id ?? "";
  }, [selectedFrom, phoneById, recommendation, sortedNumbers]);

  const senderMismatch = useSenderMismatch({
    selected: selectedFrom,
    effective: effectiveFrom,
    recommendation
  });

  const mismatchDialog = recommendation ? (
    <SenderMismatchDialog
      opened={senderMismatch.dialogOpen}
      channel="sms"
      recommendation={recommendation}
      currentLabel={phoneById.get(effectiveFrom)}
      recommendedLabel={phoneById.get(recommendation.value)}
      onKeep={senderMismatch.dismiss}
      onSwitch={() => setSelectedFrom(recommendation.value)}
      zIndex={embed?.modalZIndex}
    />
  ) : null;

  const fromStyles = (styles: StylesConfig) =>
    senderMismatch.mismatched ? withWarningBorder(styles) : styles;

  const effectiveFromText = useMemo(() => {
    const phoneNumber = sortedNumbers.find(
      (candidate) => candidate.communication_service_id === effectiveFrom
    );
    if (!phoneNumber) return "";
    return (
      formatPhone(phoneNumber.phone_number ?? "") || (phoneNumber.name ?? "")
    );
  }, [sortedNumbers, effectiveFrom]);

  const fromPill = footerFromPillProps({
    isNarrow,
    valueIcon: PHONE_VALUE_ICON
  });

  const effectiveTo = useMemo(() => {
    // Keep an explicit selection only while it is still selectable (a live
    // STOP can disable the previously-picked number).
    if (
      selectedTo &&
      toOptions.some(
        (option) => option.value === selectedTo && !option.isDisabled
      )
    )
      return selectedTo;
    // Prefer the first number that has not opted out; fall back to the first
    // number so an all-opted-out contact still shows a (blocked) value that the
    // composer explains rather than silently clearing the field.
    const firstEnabled = toOptions.find((option) => !option.isDisabled)?.value;
    const fallback = firstEnabled ?? toOptions[0]?.value;
    return typeof fallback === "string" ? fallback : "";
  }, [selectedTo, toOptions]);

  const isToOptedOut = useMemo(
    () => isPhoneOptedOut(effectiveTo, optedOutSet),
    [effectiveTo, optedOutSet]
  );

  const toUndeliverable = useMemo(
    () => findUndeliverable(effectiveTo, undeliverableMap),
    [effectiveTo, undeliverableMap]
  );

  const handleChange = useCallback(() => {
    if (effectiveFrom && effectiveTo && onSelectionChange)
      onSelectionChange(effectiveFrom, effectiveTo, {
        isToOptedOut,
        toUndeliverable
      });
  }, [
    effectiveFrom,
    effectiveTo,
    isToOptedOut,
    toUndeliverable,
    onSelectionChange
  ]);

  useEffect(() => {
    handleChange();
  }, [handleChange]);

  const hasNoFromNumbers = !isLoadingNumbers && fromOptions.length === 0;
  const hasNoToPhones = toOptions.length === 0;

  if (compact)
    return (
      <CompactRoutingRow>
        {hasNoFromNumbers ? (
          <CompactRoutingEmpty
            label="From"
            message="No phone numbers available"
          />
        ) : (
          <CompactRoutingField
            id="from-phone"
            label="From"
            value={effectiveFrom}
            valueText={effectiveFromText}
            options={fromOptions}
            onChange={setSelectedFrom}
            isDisabled={fromOptions.length === 0}
            warning={senderMismatch.mismatched}
            menuPlacement={menuPlacement}
            placeholder="Select..."
          />
        )}
        {hasNoToPhones ? (
          <CompactRoutingEmpty label="To" message="No phone number on file" />
        ) : (
          <CompactRoutingField
            id="to-phone"
            label="To"
            value={effectiveTo}
            valueText={formatPhone(effectiveTo)}
            options={toOptions}
            onChange={setSelectedTo}
            menuPlacement={menuPlacement}
          />
        )}
        {mismatchDialog}
      </CompactRoutingRow>
    );

  let toField = (
    <Select
      id="to-phone"
      hideOptional
      searchable={false}
      value={effectiveTo}
      onChange={(event) => setSelectedTo(event.target.value)}
      options={toOptions}
      className="!border-0 !shadow-none !bg-transparent !text-content-1000 !font-medium !p-0"
      legacyErrorMargins={false}
      menuPlacement={menuPlacement}
    />
  );
  if (hasNoToPhones)
    toField = (
      <Select
        id="to-phone"
        hideOptional
        searchable={false}
        value=""
        onChange={() => {}}
        options={[]}
        className={footerPillClassName({ isNarrow })}
        styles={FOOTER_PILL_STYLES}
        legacyErrorMargins={false}
        isDisabled
        menuPlacement={menuPlacement}
        placeholder="No phone on file"
      />
    );
  else if (multiRecipient && contactId)
    toField = (
      <ToRecipientsSelect
        channel="sms"
        contactId={Number(contactId)}
        contactName={
          [contact?.first_name, contact?.last_name]
            .filter(Boolean)
            .join(" ")
            .trim() || ""
        }
        addresses={toOptions
          .filter((option) => typeof option.value === "string")
          .map((option) => ({
            value: option.value as string,
            disabled: option.isDisabled,
            disabledMessage: option.disabledMessage
          }))}
        selectedAddress={effectiveTo}
        onSelectAddress={setSelectedTo}
        extraRecipients={multiRecipient.extraRecipients}
        onAddRecipient={multiRecipient.onAddRecipient}
        onRemoveRecipient={multiRecipient.onRemoveRecipient}
        primaryExcluded={multiRecipient.primaryExcluded}
        onPrimaryExcludedChange={multiRecipient.onPrimaryExcludedChange}
      />
    );

  return (
    <>
      <RoutingFieldsRow
        from={
          <Select
            id="from-phone"
            hideOptional
            searchable={false}
            value={effectiveFrom}
            onChange={(event) => setSelectedFrom(event.target.value)}
            options={fromOptions}
            {...fromPill}
            styles={fromStyles(fromPill.styles)}
            menuWidth={senderMenuWidth({ recommendation, isNarrow })}
            legacyErrorMargins={false}
            isDisabled={isLoadingNumbers || fromOptions.length === 0}
            menuPlacement={menuPlacement}
            placeholder={
              hasNoFromNumbers ? "No phone numbers available" : "Select..."
            }
          />
        }
        to={toField}
      />
      {mismatchDialog}
    </>
  );
};
